import { Star } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import Autoplay from "embla-carousel-autoplay";
import { useRef, useState, useEffect } from "react";
import type { CarouselApi } from "@/components/ui/carousel";

const testimonials = [
  {
    quote: "Text.onl has completely transformed how I create captions. My Instagram posts stand out so much more now!",
    name: "Sarah M.",
    platform: "Instagram Creator",
    rating: 5,
    gradient: "from-purple-600 via-pink-500 to-orange-400",
  },
  {
    quote: "The fancy text styles are perfect for making my TikTok bios pop. My followers always ask how I do it!",
    name: "Jake T.",
    platform: "TikTok Influencer",
    rating: 5,
    gradient: "from-cyan-400 via-pink-500 to-red-500",
  },
  {
    quote: "I use this daily for my Discord server announcements. The Unicode lookup tool is incredibly useful.",
    name: "Alex K.",
    platform: "Discord Server Owner",
    rating: 5,
    gradient: "from-indigo-500 to-purple-600",
  },
  {
    quote: "Finally found a tool that works on all platforms. The batch transformer saves me hours every week.",
    name: "Emma R.",
    platform: "Social Media Manager",
    rating: 5,
    gradient: "from-blue-500 to-teal-400",
  },
  {
    quote: "Love how easy it is to copy styled text. My Twitter threads look so much more professional now.",
    name: "Marcus L.",
    platform: "Twitter Creator",
    rating: 5,
    gradient: "from-sky-400 to-blue-600",
  },
  {
    quote: "The QR code generator and social preview tools are game changers for my content strategy.",
    name: "Priya S.",
    platform: "YouTube Creator",
    rating: 5,
    gradient: "from-red-500 to-rose-600",
  },
];

const TestimonialsCarousel = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const autoplayRef = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-primary/5" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
            What Creators Say
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Join thousands of content creators who trust Text.onl for their styling needs.
          </p>
        </div>

        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          plugins={[autoplayRef.current]}
          className="w-full max-w-5xl mx-auto"
        >
          <CarouselContent className="-ml-4">
            {testimonials.map((testimonial, index) => (
              <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Card className="p-6 h-full bg-card/50 backdrop-blur-sm border-primary/10 hover:border-primary/20 transition-colors">
                  {/* Stars */}
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-foreground/90 mb-4 leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="mt-auto flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center text-white font-semibold text-sm shrink-0`}>
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.platform}
                      </p>
                    </div>
                  </div>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Navigation Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                current === index
                  ? "bg-primary w-6"
                  : "bg-primary/30 hover:bg-primary/50"
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
